/**
 * SimilarArticlesDialog - Dialog para propagar tags para artigos similares
 *
 * Mostra artigos similares ao artigo atual (score >= 90%)
 * Permite selecionar quais artigos receberão a mesma tag
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Avatar,
  useTheme
} from '@mui/material';
import {
  ContentCopy as SimilarIcon,
  LocalOffer as TagIcon,
  CheckCircle as SuccessIcon,
  Article as ArticleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const SimilarArticlesDialog = ({
  open,
  onClose,
  articleId,
  tagId,
  tagName,
  onSuccess
}) => {
  const theme = useTheme();
  const [similarArticles, setSimilarArticles] = useState([]);
  const [sourceArticle, setSourceArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedArticles, setSelectedArticles] = useState([]);

  useEffect(() => {
    if (open && articleId && tagId) {
      fetchSimilarArticles();
      setSelectedArticles([]);
      setError(null);
      setSuccess(null);
    }
  }, [open, articleId, tagId]);

  const fetchSimilarArticles = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.post(`/articles/${articleId}/similar`, {
        minScore: 50,  // Reduzido para encontrar mais artigos similares
        limit: 50,
        excludeTagId: tagId
      });

      if (res.data.success) {
        setSourceArticle(res.data.sourceArticle);
        setSimilarArticles(res.data.similarArticles || []);

        if (res.data.totalFound === 0) {
          setError('Nenhum artigo similar encontrado');
        }
      } else {
        setError(res.data.error || 'Erro ao buscar artigos similares');
      }
    } catch (err) {
      console.error('Erro ao buscar artigos similares:', err);
      setError(err.response?.data?.error || 'Erro ao buscar artigos similares');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleArticle = (articleId) => {
    setSelectedArticles(prev => {
      if (prev.includes(articleId)) {
        return prev.filter(id => id !== articleId);
      }
      return [...prev, articleId];
    });
  };

  const handleSelectAll = () => {
    if (selectedArticles.length === similarArticles.length) {
      setSelectedArticles([]);
    } else {
      setSelectedArticles(similarArticles.map(a => a._id));
    }
  };

  const handleAddTag = async () => {
    if (selectedArticles.length === 0) return;

    try {
      setSaving(true);
      setError(null);

      const res = await api.post('/articles/bulk-add-tag', {
        articleIds: selectedArticles,
        tagId
      });

      if (res.data.success) {
        setSuccess(`Tag adicionada a ${res.data.modifiedCount} artigos`);
        setTimeout(() => {
          onSuccess?.(res.data.modifiedCount);
          onClose();
        }, 1500);
      } else {
        setError(res.data.error || 'Erro ao adicionar tag');
      }
    } catch (err) {
      console.error('Erro ao adicionar tag:', err);
      setError(err.response?.data?.error || 'Erro ao adicionar tag');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  return (
    <PlatformDialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <PlatformDialogTitle
        title="Artigos Similares"
        subtitle={tagName ? `Propagar tag: ${tagName}` : undefined}
        icon={<SimilarIcon color="primary" />}
        onClose={handleClose}
      >
        {tagName && (
          <Chip
            icon={<TagIcon />}
            label={tagName}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ ml: 1, borderRadius: 1 }}
          />
        )}
      </PlatformDialogTitle>

      <DialogContent sx={{ pt: 3.5 }}>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <CircularProgress size={40} />
            <Typography variant="body2" color="text.secondary" mt={2}>
              Buscando artigos similares...
            </Typography>
          </Box>
        ) : error && similarArticles.length === 0 ? (
          <Alert severity="info" sx={{ my: 2 }}>
            {error}
          </Alert>
        ) : success ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={4}>
            <SuccessIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="success.main">
              {success}
            </Typography>
          </Box>
        ) : (
          <>
            <Box mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Encontramos <strong>{similarArticles.length}</strong> artigos com títulos similares.
                Deseja adicionar a tag a estes artigos?
              </Typography>
            </Box>

            {similarArticles.length > 0 && (
              <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
                <Button
                  size="small"
                  onClick={handleSelectAll}
                  startIcon={<Checkbox
                    checked={selectedArticles.length === similarArticles.length}
                    indeterminate={selectedArticles.length > 0 && selectedArticles.length < similarArticles.length}
                    size="small"
                  />}
                >
                  {selectedArticles.length === similarArticles.length ? 'Desmarcar todos' : 'Selecionar todos'}
                </Button>
                <Typography variant="body2" color="text.secondary">
                  {selectedArticles.length} selecionados
                </Typography>
              </Box>
            )}

            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {similarArticles.map((article) => (
                <ListItem
                  key={article._id}
                  disablePadding
                  sx={{
                    mb: 0.5,
                    borderRadius: 1,
                    bgcolor: selectedArticles.includes(article._id)
                      ? `${theme.palette.primary.main}10`
                      : 'transparent',
                    '&:hover': {
                      bgcolor: `${theme.palette.primary.main}08`
                    }
                  }}
                >
                  <ListItemButton onClick={() => handleToggleArticle(article._id)} dense>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Checkbox
                        edge="start"
                        checked={selectedArticles.includes(article._id)}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    </ListItemIcon>
                    {article.image ? (
                      <Avatar
                        src={article.image}
                        variant="rounded"
                        sx={{ width: 40, height: 40, mr: 1.5 }}
                      />
                    ) : (
                      <Avatar
                        variant="rounded"
                        sx={{ width: 40, height: 40, mr: 1.5, bgcolor: 'grey.200' }}
                      >
                        <ArticleIcon fontSize="small" color="disabled" />
                      </Avatar>
                    )}
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {article.title}
                        </Typography>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary">
                            {article.siteName || 'Fonte desconhecida'}
                          </Typography>
                          <Chip
                            label={`${article.similarityScore}%`}
                            size="small"
                            color={article.similarityScore >= 95 ? 'success' : 'default'}
                            sx={{ height: 18, fontSize: 10 }}
                          />
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
        <Button onClick={handleClose} disabled={saving}>
          {success ? 'Fechar' : 'Cancelar'}
        </Button>
        {!success && similarArticles.length > 0 && (
          <Button
            variant="contained"
            onClick={handleAddTag}
            disabled={saving || selectedArticles.length === 0}
            startIcon={saving ? <CircularProgress size={16} /> : <TagIcon />}
          >
            {saving ? 'Adicionando...' : `Adicionar tag (${selectedArticles.length})`}
          </Button>
        )}
      </DialogActions>

      {saving && <LinearProgress />}
    </PlatformDialog>
  );
};

export default SimilarArticlesDialog;

